import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  SetShippingMethodsOnCartInput,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutation_Shipping_SetShippingMethodsOnCart_QueryKey } from "./data-mutation--shipping-set-shipping-methods-on-cart--query-key-factory";

export interface DataMutationShippingSetShippingMethodsOnCartConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationShippingSetShippingMethodsOnCart_Result["data"]
  > {
  data: DataMutationShippingSetShippingMethodsOnCart_Result["data"];
  status: DataMutationShippingSetShippingMethodsOnCart_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationShippingSetShippingMethodsOnCartConsumerInterface;
const DataMutationShippingSetShippingMethodsOnCartContext =
  React.createContext(defaultValue);

export interface DataMutationShippingSetShippingMethodsOnCart_ControllerInterface {
  mutate: DataMutationShippingSetShippingMethodsOnCart_Result["mutate"];
  mutateAsync: DataMutationShippingSetShippingMethodsOnCart_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationShippingSetShippingMethodsOnCart_ControllerInterface;
const DataMutationShippingSetShippingMethodsOnCart_ControllerContext =
  React.createContext(controllerDefaultValue);

export interface DataMutationShippingSetShippingMethodsOnCart_Request
  extends SetShippingMethodsOnCartInput {}
export type DataMutationShippingSetShippingMethodsOnCart_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationShippingSetShippingMethodsOnCart_Request,
    unknown
  >;
export type GraphqlResponse_ShippingSetShippingMethodsOnCart =
  GraphqlWrappedData<
    "setShippingMethodsOnCart",
    {
      cart: CheckoutModel_MyBag;
    }
  >;

export interface DataMutationShippingSetShippingMethodsOnCartProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationShippingSetShippingMethodsOnCartProvider: React.FunctionComponent<
  DataMutationShippingSetShippingMethodsOnCartProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_Shipping_SetShippingMethodsOnCart_QueryKey.shippingSetShippingMethodsOnCart();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationShippingSetShippingMethodsOnCart_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          setShippingMethodsOnCart(input: {
            cart_id: "${request.cart_id}"
            shipping_methods: [
              ${request.shipping_methods
                .map(
                  (shipping_method) => `
                {
                  carrier_code: "${shipping_method.carrier_code}"
                  method_code: "${shipping_method.method_code}"
                }
              `
                )
                .join(", ")}
            ]
          }) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } =
        await axios.post<GraphqlResponse_ShippingSetShippingMethodsOnCart>(
          "/graphql",
          { query: gqlQuery },
          { headers }
        );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { setShippingMethodsOnCart } = gqlResponseData || {};
      const { cart } = setShippingMethodsOnCart || {};

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const ignoreMyCurrentState =
    genericDataPortController.ignoreMyCurrentStateFactory(mutation);
  const value: DataMutationShippingSetShippingMethodsOnCartConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
        error: mutation.error,

        dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
        errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState: ignoreMyCurrentState,
      }),
      [
        mutation.data,
        mutation.status,
        mutation.error,

        genericDataPortValues.dataUpdatedAt,
        genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState,
      ]
    );
  const controllerValue: DataMutationShippingSetShippingMethodsOnCart_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationShippingSetShippingMethodsOnCartContext.Provider value={value}>
      <DataMutationShippingSetShippingMethodsOnCart_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationShippingSetShippingMethodsOnCart_ControllerContext.Provider>
    </DataMutationShippingSetShippingMethodsOnCartContext.Provider>
  );
};

export const useDataMutationShippingSetShippingMethodsOnCart = () => {
  const context = React.useContext(
    DataMutationShippingSetShippingMethodsOnCartContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationShippingSetShippingMethodsOnCart must be used within a DataMutationShippingSetShippingMethodsOnCartProvider"
    );
  }
  return context;
};

export const useDataMutationShippingSetShippingMethodsOnCart_asController =
  () => {
    const context = React.useContext(
      DataMutationShippingSetShippingMethodsOnCart_ControllerContext
    );
    if (context === controllerDefaultValue) {
      throw new Error(
        "useDataMutationShippingSetShippingMethodsOnCart_asController must be used within a DataMutationShippingSetShippingMethodsOnCartProvider"
      );
    }
    return context;
  };
